import React from "react";
import { Minus, Plus } from "lucide-react";
import { getProductMaxCartQuantity } from "../../context/CartContext";
import {
  getLineItemUnitPrice,
  cartLineKey,
} from "../../utils/productVariants";

const OrderSummary = ({
  cart,
  updateQuantity,
  shippingFee,
  /** Raw cart shipping total before pickup waiver (for strikethrough display). */
  cartShippingFee,
  receivingMethod = "delivery",
  gstTotal = 0,
  couponDiscount,
  subtotal,
  finalPayableRupees,
  embedded = false,
}) => {
  const rawShip =
    cartShippingFee != null && Number.isFinite(Number(cartShippingFee))
      ? Number(cartShippingFee)
      : Number(shippingFee);
  const toPay =
    finalPayableRupees != null && Number.isFinite(finalPayableRupees)
      ? finalPayableRupees
      : Math.max(1, subtotal + shippingFee + gstTotal - couponDiscount);

  const shell =
    embedded
      ? "rounded-xl border border-stone-100 bg-stone-50/40 p-4 sm:p-5"
      : "rounded-2xl border border-stone-200/80 bg-white p-4 shadow-lg shadow-stone-200/50 sm:p-5";

  return (
    <div className={shell}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h3 className="text-base font-bold tracking-tight text-stone-900 sm:text-lg">
          Your order
        </h3>
        <span className="rounded-full bg-orange-100 px-2.5 py-0.5 text-xs font-semibold text-orange-800">
          {cart.reduce((n, { quantity }) => n + quantity, 0)} items
        </span>
      </div>

      <ul className="max-h-[min(50vh,22rem)] space-y-3 overflow-y-auto pr-1 sm:max-h-none">
        {cart.map(({ product, quantity, variantOptionId }, idx) => {
          const price = getLineItemUnitPrice(product, variantOptionId);
          const line = price * quantity;
          const maxQ = getProductMaxCartQuantity(product, variantOptionId);
          const atMax = Number.isFinite(maxQ) && quantity >= maxQ;
          return (
            <li
              key={cartLineKey(product._id, variantOptionId) || idx}
              className="flex gap-3 rounded-xl border border-stone-100 bg-white p-2.5 shadow-sm"
            >
              <img
                src={product.images[0]}
                alt={product.name}
                className="h-16 w-16 shrink-0 rounded-lg object-cover ring-1 ring-black/5 sm:h-[4.5rem] sm:w-[4.5rem]"
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold leading-snug text-stone-900 line-clamp-2">
                  {product.name}
                </p>
                {variantOptionId ? (
                  <p className="mt-0.5 text-xs font-medium text-stone-500">
                    {product.variantGroup?.name ? `${product.variantGroup.name}: ` : ""}
                    {product.variantGroup?.options?.find(
                      (o) => String(o.optionId) === String(variantOptionId),
                    )?.label || "Option"}
                  </p>
                ) : null}
                <p className="mt-0.5 text-sm text-stone-500">
                  ₹{Number(price).toFixed(0)} each
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-stone-700 transition hover:bg-stone-100 active:scale-95"
                    onClick={() =>
                      updateQuantity(product._id, -1, variantOptionId || "")
                    }
                    aria-label="Decrease quantity"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="min-w-[1.5rem] text-center text-sm font-bold tabular-nums text-stone-900">
                    {quantity}
                  </span>
                  <button
                    type="button"
                    disabled={atMax}
                    title={atMax ? `Only ${maxQ} in stock` : "Increase quantity"}
                    className="flex h-8 w-8 items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-stone-700 transition hover:bg-stone-100 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() =>
                      updateQuantity(product._id, 1, variantOptionId || "")
                    }
                    aria-label="Increase quantity"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-bold tabular-nums text-stone-900">
                  ₹{line.toFixed(0)}
                </p>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="mt-4 space-y-2 border-t border-stone-200/80 pt-4 text-sm">
        <div className="flex justify-between text-stone-600">
          <span>Subtotal</span>
          <span className="font-medium tabular-nums text-stone-800">
            ₹{subtotal.toFixed(0)}
          </span>
        </div>
        <div className="flex justify-between text-stone-600">
          <span>
            {receivingMethod === "pickup"
              ? "Delivery fee (self pickup)"
              : "Delivery & packing"}
          </span>
          <span className="font-medium tabular-nums text-stone-800">
            {receivingMethod === "pickup" && rawShip > 0 ? (
              <span className="inline-flex flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
                <span className="text-stone-400 line-through">₹{rawShip.toFixed(0)}</span>
                <span className="font-semibold text-emerald-700">Waived</span>
              </span>
            ) : shippingFee > 0 ? (
              `₹${shippingFee.toFixed(0)}`
            ) : (
              "Free"
            )}
          </span>
        </div>
        {couponDiscount > 0 && (
          <div className="flex justify-between font-medium text-emerald-700">
            <span>Offer applied</span>
            <span className="tabular-nums">− ₹{couponDiscount.toFixed(0)}</span>
          </div>
        )}
        <div className="flex items-baseline justify-between border-t border-dashed border-stone-200 pt-3">
          <span className="text-base font-bold text-stone-900">To pay</span>
          <span className="text-xl font-extrabold tabular-nums text-orange-600">
            ₹{toPay.toFixed(0)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
